// src/hooks/useSubscription.js
// Offline-first subscription state management.
// Uses localStorage as primary store; syncs with server when online.
// Notifies service worker on every state change for background sync.

import { useState, useEffect, useCallback } from 'react';
import { API_URL, STRIPE_PRO_URL, STRIPE_TEAM_URL } from '../config';

// ---- Tier definitions ----
export const TIERS = {
  free: {
    key: 'free',
    label: 'Free',
    monthlyLimit: 3,
    windowLabel: 'questo mese',
    color: '#00c8d4',
    badgeClass: 'sub-badge--free',
  },
  pro: {
    key: 'pro',
    label: 'Pro',
    monthlyLimit: 100,
    windowLabel: 'questo mese',
    price: 9,
    color: '#a855f7',
    badgeClass: 'sub-badge--pro',
    stripeUrl: STRIPE_PRO_URL,
  },
  team: {
    key: 'team',
    label: 'Team',
    monthlyLimit: 500,
    windowLabel: 'questo mese',
    price: 29,
    color: '#34d399',
    badgeClass: 'sub-badge--team',
    stripeUrl: STRIPE_TEAM_URL,
  },
};

const SUB_KEY   = 'trm_subscription_v1';
const USAGE_KEY = 'trm_usage_v1';

// ---- Helpers ----
const currentMonth = () => new Date().toISOString().slice(0, 7);

const readSub = () => {
  try {
    const raw = localStorage.getItem(SUB_KEY);
    return raw ? JSON.parse(raw) : { tier: 'free', teamCode: null, lastSyncedAt: null, token: null };
  } catch {
    return { tier: 'free', teamCode: null, lastSyncedAt: null, token: null };
  }
};

const readUsage = () => {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (!raw) return { count: 0, month: currentMonth() };
    const data = JSON.parse(raw);
    // Auto-reset on month rollover
    if (data.month !== currentMonth()) return { count: 0, month: currentMonth() };
    return data;
  } catch {
    return { count: 0, month: currentMonth() };
  }
};

const persist = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* storage full — graceful noop */ }
};

const notifySW = (type, payload) => {
  try {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type, payload });
    }
  } catch { /* SW not available */ }
};

// ---- Hook ----
export const useSubscription = () => {
  const [sub,   setSub]   = useState(readSub);
  const [usage, setUsage] = useState(readUsage);

  const tier            = TIERS[sub.tier] || TIERS.free;
  const usageCount      = usage.count;
  const usageRemaining  = Math.max(0, tier.monthlyLimit - usageCount);
  const usageLimitReached = usageCount >= tier.monthlyLimit;
  const usagePct = Math.min(100, Math.round((usageCount / tier.monthlyLimit) * 100));

  // Persist usage whenever it changes
  useEffect(() => {
    persist(USAGE_KEY, usage);
    notifySW('USAGE_UPDATE', usage);
  }, [usage]);

  // Persist subscription whenever it changes
  useEffect(() => {
    persist(SUB_KEY, sub);
    notifySW('SUBSCRIPTION_UPDATE', sub);
  }, [sub]);

  // Sync usage count from server when Pro token is set (cross-device)
  useEffect(() => {
    if (!sub.token) return;
    fetch(`${API_URL}/subscription/usage?token=${encodeURIComponent(sub.token)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.usageMonth === currentMonth() && typeof data.usageCount === 'number') {
          setUsage({ count: data.usageCount, month: data.usageMonth });
        }
      })
      .catch(() => { /* offline — keep local */ });
  }, [sub.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Online sync: re-validate subscription with server
  useEffect(() => {
    const syncWithServer = async () => {
      if (!navigator.onLine) return;
      try {
        const res = await fetch(`${API_URL}/subscription/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tier: sub.tier,
            token: sub.token || null,
            teamCode: sub.teamCode,
            usageMonth: usage.month,
            usageCount: usage.count,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.tier && data.tier !== sub.tier) {
            setSub(prev => ({ ...prev, tier: data.tier, lastSyncedAt: new Date().toISOString() }));
          }
          if (data.token && data.token !== sub.token) {
            setSub(prev => ({ ...prev, token: data.token, lastSyncedAt: new Date().toISOString() }));
          }
        }
      } catch { /* offline or endpoint not yet implemented — noop */ }
    };

    window.addEventListener('online', syncWithServer);
    syncWithServer();
    return () => window.removeEventListener('online', syncWithServer);
  }, [sub.token, sub.tier]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Actions ----
  const incrementUsage = useCallback(() => {
    setUsage(prev => {
      const m = currentMonth();
      const base = prev.month !== m ? 0 : prev.count;
      return { count: base + 1, month: m };
    });
  }, []);

  const activatePro = useCallback(() => {
    setSub(prev => ({ ...prev, tier: 'pro', lastSyncedAt: new Date().toISOString() }));
  }, []);

  const restoreToken = useCallback((token) => {
    setSub(prev => {
      const updated = { ...prev, token, lastSyncedAt: new Date().toISOString() };
      notifySW('SUBSCRIPTION_UPDATE', updated);
      return updated;
    });
  }, []);

  const activateTeam = useCallback((teamCode) => {
    setSub(prev => ({
      ...prev,
      tier: 'team',
      teamCode: teamCode || prev.teamCode,
      lastSyncedAt: new Date().toISOString(),
    }));
  }, []);

  const joinTeam = useCallback((code) => {
    setSub(prev => ({ ...prev, teamCode: code, tier: 'team', lastSyncedAt: new Date().toISOString() }));
  }, []);

  const generateTeamCode = useCallback(() => {
    const code = 'TRM-' + Math.random().toString(36).slice(2, 8).toUpperCase();
    setSub(prev => ({ ...prev, teamCode: code }));
    return code;
  }, []);

  return {
    sub,
    tier,
    usageCount,
    usageRemaining,
    usageLimitReached,
    usagePct,
    incrementUsage,
    activatePro,
    activateTeam,
    joinTeam,
    generateTeamCode,
    restoreToken,
    TIERS,
  };
};
